import { Check, Sparkles } from 'lucide-react';

const REGISTER_URL = 'https://app.theonestack.com/research';

const tiers = [
  {
    name: 'Free',
    price: '$0',
    sub: 'forever',
    desc: 'Sign up free. Get $5 in usage credits to try it on real research.',
    accent: 'border-rose-500 ring-2 ring-rose-500/30 bg-rose-500/5',
    cta: 'Start free with $5 credit',
    ctaStyle: 'bg-rose-500 text-white hover:bg-rose-400',
    badge: '$5 starter credit',
    features: [
      'No credit card to start',
      'Full web channel access',
      'CRM push',
      'Source citations on every fact',
      'All four built-in templates',
      '$5 in usage credit on signup',
      'Pay-as-you-go after credit is used',
    ],
  },
  {
    name: 'Pay-as-you-go',
    price: 'Usage',
    sub: 'no minimums',
    desc: 'Once your $5 credit is used, you only pay for what you actually run. No subscription, no platform fee.',
    accent: 'border-white/10',
    cta: 'See rate card',
    ctaStyle: 'bg-white/10 text-white hover:bg-white/15',
    features: [
      'Everything in Free',
      'Voice channel (THEO outbound)',
      'Email channel (SES)',
      'Saved playbooks',
      'Per-job cost caps',
      'Per-action transparent pricing',
      'No commitment',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    sub: 'with volume credits',
    desc: 'White-label, BYOK external AI, dedicated support, custom data sources.',
    accent: 'border-white/10',
    cta: 'Talk to us',
    ctaStyle: 'bg-white/10 text-white hover:bg-white/15',
    features: [
      'Everything in Pay-as-you-go',
      'White-label resale',
      'BYOK Perplexity / OpenAI / Anthropic',
      'Custom data sources',
      'Custom integrations',
      'SLA + custom DPA',
      'Priority feature requests',
    ],
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="relative bg-ink-950 py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-rose-400">
            Pricing
          </p>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Free to start.<br className="sm:hidden" />{' '}
            Pay only for what you run.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            No subscription. No platform fee. No seat fee. Sign up, get $5 in
            usage credit, and only pay when you've used it up. Every action —
            web search, voice minute, email send — is metered transparently
            in your dashboard.
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`relative rounded-2xl border bg-ink-900/60 p-8 ${t.accent}`}
            >
              {t.badge && (
                <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1 text-xs font-semibold text-white shadow-lg shadow-rose-500/30">
                  <Sparkles className="h-3 w-3" />
                  {t.badge}
                </span>
              )}
              <div className="mb-6">
                <h3 className="mb-2 text-2xl font-bold text-white">{t.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black tracking-tight text-white">
                    {t.price}
                  </span>
                  <span className="text-sm text-slate-400">{t.sub}</span>
                </div>
                <p className="mt-3 text-sm text-slate-400">{t.desc}</p>
              </div>

              <a
                href={REGISTER_URL}
                className={`mb-8 block w-full rounded-xl px-6 py-3 text-center text-sm font-semibold transition ${t.ctaStyle}`}
              >
                {t.cta}
              </a>

              <ul className="space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 mx-auto max-w-2xl rounded-2xl border border-white/5 bg-ink-900/40 p-6 text-center">
          <p className="mb-2 text-sm font-semibold text-white">Sample real-world cost</p>
          <p className="text-sm text-slate-400">
            Find 78 verified decision-makers at small businesses in a US metro,
            phone-verify the top 8, push everything to your CRM with tags +
            pipeline + owner: <span className="font-semibold text-rose-400">~$5–15</span>.
            That single job often falls within your starter credit.
          </p>
        </div>
      </div>
    </section>
  );
}
